// apps/client/src/hooks/office/useMonthlyAggregatesOffice.ts
// OFFICE DASHBOARD SPECIFIC - Year-based filtering only (no relative periods)
// Separated from main dashboard hooks for independent evolution
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo } from 'react';
import { MonthlyAggregate } from '../../types';
import { useDashboardQuery } from '../useDashboardQuery';

interface UseMonthlyAggregatesOfficeParams {
  companyId: string;
  year: number;
  yearType: 'calendar' | 'financial';
  officeId?: string;
  templateId?: string;
  entityCode?: string;
}

interface UseMonthlyAggregatesOfficeReturn {
  aggregates: MonthlyAggregate[];
  loading: boolean;
  error: string | null;
  upfrontCommission: number;
  trailCommission: number;
  templateIds: string[];
}

interface MonthlyCommissionsResponse {
  months: Array<{
    month: string;
    upfrontNetCents: number;
    trailNetCents: number;
    byBroker?: Record<string, {
      brokerDisplay: string;
      upfrontNetCents: number;
      trailNetCents: number;
    }>;
    byLender?: Record<string, {
      upfrontNetCents: number;
      trailNetCents: number;
      totalLoanAmountCents: number;
      lineOfTrail: number;
    }>;
  }>;
  totals: {
    upfrontCommission: number;
    trailCommission: number;
  };
  templateIds: string[];
}

/**
 * Office Dashboard hook to fetch monthly commission aggregates
 * Uses year-based filtering only (calendar or financial year)
 */
export function useMonthlyAggregatesOffice({
  companyId,
  year,
  yearType,
  officeId,
  templateId,
  entityCode,
}: UseMonthlyAggregatesOfficeParams): UseMonthlyAggregatesOfficeReturn {
  const { data, loading, error } = useDashboardQuery<MonthlyCommissionsResponse>(
    'getMonthlyCommissions',
    {
      companyId,
      year,
      yearType,
      officeId,
      templateId,
      entityCode,
    },
    { enabled: !!companyId }
  );

  const templateIds = useMemo(() => data?.templateIds || [], [data]);

  const aggregates = useMemo<MonthlyAggregate[]>(() => {
    if (!data) return [];

    return data.months.map((item) => {
      const [yearStr, monthStr] = item.month.split('-');

      const byBroker = item.byBroker
        ? Object.fromEntries(
            Object.entries(item.byBroker).map(([brokerId, brokerData]) => [
              brokerId,
              {
                brokerDisplay: brokerData.brokerDisplay,
                brokerId,
                upfrontGross: brokerData.upfrontNetCents,
                upfrontAdjustments: 0,
                upfrontNet: brokerData.upfrontNetCents,
                trailGross: brokerData.trailNetCents,
                trailAdjustments: 0,
                trailNet: brokerData.trailNetCents,
                totalLoanAmount: 0,
                lineOfTrail: 0,
                recordCount: 0,
              },
            ])
          )
        : undefined;

      const byLender = item.byLender
        ? Object.fromEntries(
            Object.entries(item.byLender).map(([lender, lenderData]) => [
              lender,
              {
                upfrontGross: lenderData.upfrontNetCents,
                upfrontAdjustments: 0,
                upfrontNet: lenderData.upfrontNetCents,
                trailGross: lenderData.trailNetCents,
                trailAdjustments: 0,
                trailNet: lenderData.trailNetCents,
                totalLoanAmount: lenderData.totalLoanAmountCents || 0,
                lineOfTrail: lenderData.lineOfTrail || 0,
                recordCount: 0,
              },
            ])
          )
        : undefined;

      return {
        yearMonth: item.month,
        year: parseInt(yearStr, 10),
        month: parseInt(monthStr, 10),
        upfrontCommission: item.upfrontNetCents,
        trailCommission: item.trailNetCents,
        totalLoanAmount: 0,
        recordCount: 0,
        byBroker,
        byLender,
      };
    });
  }, [data]);

  const upfrontCommission = aggregates.reduce((sum, agg) => sum + (agg.upfrontCommission || 0), 0);
  const trailCommission = aggregates.reduce((sum, agg) => sum + (agg.trailCommission || 0), 0);

  return {
    aggregates,
    loading,
    error,
    upfrontCommission,
    trailCommission,
    templateIds,
  };
}
